/**
 * needs-you — THE definition of "how many orders need me right now".
 *
 * Before this file five surfaces answered that question, and they answered it
 * five different ways:
 *
 *  - the nav badge hand-summed `pending_payment_verification +
 *    paid_less_than_required` and silently omitted `pending_seller_confirmation`,
 *    the commonest one of the three;
 *  - the mobile dashboard computed `newOrders + ordersInFollowUp`, which DOUBLE
 *    COUNTS — the needs-attention statuses are a subset of the pending family,
 *    so every order it was warning about was counted twice;
 *  - the desktop Orders chip read the backend `needs_attention` bucket;
 *  - `OrdersNeedsAttention` summed only the reason groups present in the first
 *    30 rows it had fetched;
 *  - the mobile Orders section summed `ATTENTION_ORDER`, six statuses including
 *    `confirmed`/`preparing`/`shipped`.
 *
 * A merchant looking at a badge saying 3, a chip saying 5 and a card saying 9
 * cannot trust any of them. Every badge now calls {@link selectNeedsYouCount}.
 *
 * The chosen set is the backend's `needs_attention` smart bucket — the one the
 * API exports, the one the "تحتاج انتباه" copy names, and the one the orders
 * list's `bucket=needs_attention` query returns. It is tier 1 ("بانتظار
 * قرارك") of the two-tier order model in `order-steps.ts` (D4), so the badge
 * and that tier's heading are one number — see docs/storefront-analytics.md.
 */

import type { OrderStatus, OrderStatusCountsResponse } from "@/lib/api/types";

/**
 * The three statuses where the seller has the next move: verify a payment,
 * chase a short payment, or confirm the order. Mirrors the backend's
 * `ORDER_SMART_BUCKETS.needs_attention`.
 *
 * This is the FALLBACK path only. The primary source is the `buckets` array the
 * status-counts endpoint returns, which carries the server's own membership —
 * so a status added to the bucket backend-side is reflected here without a
 * frontend deploy. The list exists because a response can legitimately arrive
 * without the bucket (an older cached payload), and a badge that reads 0 in
 * that case is worse than one computed from a slightly stale list.
 */
export const NEEDS_YOU_STATUSES: readonly OrderStatus[] = [
  "pending_payment_verification",
  "paid_less_than_required",
  "pending_seller_confirmation",
];

/**
 * Orders needing the seller's action right now, from one status-counts
 * response. `null`/undefined (still loading) reads as 0 rather than throwing —
 * a badge that flashes a wrong number while loading is worse than one that
 * appears a moment late.
 *
 * NOTE on scope: these counts come from `countByStoreIdGroupedByStatus`, which
 * is filtered on `{ storeId }` alone — so unlike the dashboard's analytics
 * aggregates it INCLUDES the first-run sample order and per-order-plan masked
 * orders. That is deliberate: this number labels a list the seller is about to
 * open, and that list shows those orders. A count of orders the seller can
 * already see is not a disclosure; their VALUE is, which is why every money
 * figure still goes through the analytics path's masking. See
 * docs/storefront-analytics.md.
 */
export function selectNeedsYouCount(
  data: OrderStatusCountsResponse | null | undefined,
): number {
  if (!data) {
    return 0;
  }
  const bucket = data.buckets?.find((entry) => entry.id === "needs_attention");
  if (bucket) {
    return bucket.count;
  }
  return NEEDS_YOU_STATUSES.reduce(
    (total, status) => total + (data.counts?.[status] ?? 0),
    0,
  );
}

/**
 * Short COD collections waiting for "حسنًا، سجّلته" (roundtable 2, B5). The
 * backend's `needs_attention` bucket carries them IN ADDITION to its three
 * statuses (they are `delivered` orders), so they are the bucket's count minus
 * those statuses. A response without the bucket — or a backend without the
 * COD-collection change, where the two agree — reads 0.
 */
export function selectCodShortCount(
  data: OrderStatusCountsResponse | null | undefined,
): number {
  if (typeof data?.codShortfall === "number") return Math.max(0, data.codShortfall);
  const bucket = data?.buckets?.find((entry) => entry.id === "needs_attention");
  if (!data || !bucket) return 0;
  const byStatus = NEEDS_YOU_STATUSES.reduce(
    (total, status) => total + (data.counts?.[status] ?? 0),
    0,
  );
  return Math.max(0, bucket.count - byStatus);
}
